import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, 
  Target, 
  Zap, 
  BarChart3, 
  Search, 
  Mail, 
  Users, 
  Globe,
  ArrowRight,
  CheckCircle,
  Star,
  LineChart,
  MousePointer,
  Eye,
  MessageCircle,
  Share2,
  Smartphone,
  Monitor,
  Megaphone,
  Brain,
  Bot,
  Sparkles,
  PieChart,
  TrendingDown,
  Clock
} from 'lucide-react';
import SEO from '../components/SEO';
import QivratechLogo from '../components/QivratechLogo';

const MarketingPage = () => {
  const [activeService, setActiveService] = useState(0);
  const [hoveredMetric, setHoveredMetric] = useState(null);

  const marketingServices = [
    {
      id: 'ai-driven-marketing',
      icon: <Brain className="w-16 h-16" />,
      title: "AI-Driven Digital Marketing",
      subtitle: "Intelligent Campaign Optimization",
      description: "Leverage artificial intelligence to create, optimize, and scale your digital marketing campaigns with unprecedented precision and efficiency.",
      features: [
        "AI-powered audience segmentation and targeting",
        "Automated A/B testing and campaign optimization",
        "Predictive analytics for customer behavior",
        "Dynamic content personalization at scale",
        "Real-time bid optimization for paid ads",
        "Intelligent budget allocation across channels",
        "Automated creative generation and testing",
        "Cross-channel attribution modeling"
      ],
      technologies: ["TensorFlow", "Google Analytics 4", "Facebook AI", "Adobe Sensei", "Salesforce Einstein"],
      metrics: [
        { label: "Campaign ROI", value: "300%+", improvement: "average increase" },
        { label: "Cost Per Acquisition", value: "60%", improvement: "reduction" },
        { label: "Conversion Rate", value: "180%", improvement: "improvement" },
        { label: "Time to Optimize", value: "90%", improvement: "faster" }
      ],
      color: "from-blue-400 to-cyan-500",
      bgGradient: "from-blue-500/10 to-cyan-600/10"
    },
    {
      id: 'performance-marketing',
      icon: <TrendingUp className="w-16 h-16" />,
      title: "Performance Marketing & Analytics",
      subtitle: "Data-Driven Growth Engine",
      description: "Maximize your marketing ROI with comprehensive performance marketing strategies backed by advanced analytics and real-time optimization.",
      features: [
        "Multi-channel performance tracking",
        "Advanced conversion funnel analysis",
        "Real-time dashboard and reporting",
        "Customer lifetime value optimization",
        "Attribution modeling and analysis",
        "Cohort analysis and retention metrics",
        "Revenue forecasting and planning",
        "Competitive intelligence and benchmarking"
      ],
      technologies: ["Google Analytics", "Mixpanel", "Amplitude", "Looker", "Tableau", "Power BI"],
      metrics: [
        { label: "Revenue Growth", value: "250%", improvement: "average increase" },
        { label: "Customer Acquisition", value: "40%", improvement: "cost reduction" },
        { label: "Marketing Efficiency", value: "85%", improvement: "improvement" },
        { label: "Data Accuracy", value: "99%", improvement: "precision" }
      ],
      color: "from-green-400 to-emerald-500",
      bgGradient: "from-green-500/10 to-emerald-600/10"
    },
    {
      id: 'seo-content',
      icon: <Search className="w-16 h-16" />,
      title: "SEO & Content Automation",
      subtitle: "AI-Powered Content Strategy",
      description: "Dominate search rankings with AI-driven content creation, SEO optimization, and automated content marketing workflows.",
      features: [
        "AI-powered keyword research and analysis",
        "Automated content generation and optimization",
        "Technical SEO audits and implementation",
        "Content performance tracking and optimization",
        "Local SEO and multi-location optimization",
        "Voice search optimization",
        "Featured snippet optimization",
        "Content gap analysis and strategy"
      ],
      technologies: ["GPT-4", "Jasper", "Surfer SEO", "Ahrefs", "SEMrush", "Screaming Frog"],
      metrics: [
        { label: "Organic Traffic", value: "400%", improvement: "growth" },
        { label: "Keyword Rankings", value: "75%", improvement: "in top 10" },
        { label: "Content Production", value: "500%", improvement: "faster" },
        { label: "Search Visibility", value: "300%", improvement: "increase" }
      ],
      color: "from-purple-400 to-indigo-500",
      bgGradient: "from-purple-500/10 to-indigo-600/10"
    },
    {
      id: 'crm-automation',
      icon: <Bot className="w-16 h-16" />,
      title: "CRM Integration & Automation",
      subtitle: "Intelligent Customer Journey",
      description: "Seamlessly integrate marketing automation with CRM systems to create personalized customer experiences at scale.",
      features: [
        "Advanced marketing automation workflows",
        "Lead scoring and qualification automation",
        "Personalized email marketing campaigns",
        "Customer journey mapping and optimization",
        "Sales and marketing alignment tools",
        "Behavioral trigger-based campaigns",
        "Customer segmentation and targeting",
        "Automated nurturing sequences"
      ],
      technologies: ["HubSpot", "Salesforce", "Marketo", "Pardot", "ActiveCampaign", "Zapier"],
      metrics: [
        { label: "Lead Quality", value: "220%", improvement: "increase" },
        { label: "Sales Conversion", value: "150%", improvement: "improvement" },
        { label: "Customer Retention", value: "85%", improvement: "retention rate" },
        { label: "Process Efficiency", value: "70%", improvement: "time saved" }
      ],
      color: "from-orange-400 to-red-500",
      bgGradient: "from-orange-500/10 to-red-600/10"
    }
  ];

  const processSteps = [
    {
      step: 1,
      title: "Strategy & Discovery",
      description: "Comprehensive analysis of your market, competitors, and growth opportunities",
      icon: <Target className="w-8 h-8" />,
      deliverables: ["Market Analysis", "Competitor Research", "Customer Personas", "Growth Strategy"]
    },
    {
      step: 2,
      title: "Setup & Integration",
      description: "Implementation of marketing technology stack and analytics infrastructure",
      icon: <Zap className="w-8 h-8" />,
      deliverables: ["Tech Stack Setup", "Analytics Implementation", "CRM Integration", "Automation Workflows"]
    },
    {
      step: 3,
      title: "Campaign Launch",
      description: "Multi-channel campaign execution with AI-powered optimization",
      icon: <Megaphone className="w-8 h-8" />,
      deliverables: ["Campaign Launch", "Creative Assets", "Landing Pages", "A/B Testing"]
    },
    {
      step: 4,
      title: "Optimization & Scale",
      description: "Continuous improvement and scaling based on performance data",
      icon: <TrendingUp className="w-8 h-8" />,
      deliverables: ["Performance Reports", "Optimization Plans", "Scale Strategies", "ROI Analysis"]
    }
  ];

  const marketingChannels = [
    {
      channel: "Search Engine Marketing",
      description: "Google Ads, Bing Ads, and SEO optimization",
      icon: <Search className="w-6 h-6" />,
      metrics: ["CTR: 8.5%", "CPA: $45", "ROAS: 4.2x"]
    },
    {
      channel: "Social Media Marketing",
      description: "Facebook, LinkedIn, Instagram, and Twitter advertising",
      icon: <Share2 className="w-6 h-6" />,
      metrics: ["Engagement: 12%", "CPM: $8", "CVR: 3.8%"]
    },
    {
      channel: "Email Marketing",
      description: "Automated email sequences and newsletter campaigns",
      icon: <Mail className="w-6 h-6" />,
      metrics: ["Open Rate: 28%", "CTR: 4.2%", "ROI: 38:1"]
    },
    {
      channel: "Content Marketing",
      description: "Blog posts, whitepapers, and educational content",
      icon: <MessageCircle className="w-6 h-6" />,
      metrics: ["Traffic: +280%", "Leads: +150%", "Shares: +420%"]
    },
    {
      channel: "Display Advertising",
      description: "Banner ads, retargeting, and programmatic advertising",
      icon: <Monitor className="w-6 h-6" />,
      metrics: ["Impressions: 2M+", "CTR: 1.8%", "CPA: $32"]
    },
    {
      channel: "Mobile Marketing",
      description: "App advertising and mobile-optimized campaigns",
      icon: <Smartphone className="w-6 h-6" />,
      metrics: ["Installs: 50K+", "Retention: 65%", "LTV: $120"]
    }
  ];

  const caseStudies = [
    {
      client: "SaaS Startup",
      industry: "B2B Software",
      challenge: "Generate qualified leads and accelerate growth",
      solution: "AI-driven multi-channel marketing with advanced attribution",
      results: [
        { metric: "Lead Generation", value: "400% increase" },
        { metric: "Cost Per Lead", value: "65% reduction" },
        { metric: "Sales Qualified Leads", value: "300% growth" },
        { metric: "Revenue Growth", value: "250% increase" }
      ],
      timeline: "6 months"
    },
    {
      client: "E-commerce Brand",
      industry: "Consumer Goods",
      challenge: "Scale online sales while maintaining profitability",
      solution: "Performance marketing with AI-powered optimization",
      results: [
        { metric: "Revenue", value: "500% growth" },
        { metric: "ROAS", value: "6.2x average" },
        { metric: "Customer Acquisition", value: "300% increase" },
        { metric: "Market Share", value: "150% growth" }
      ],
      timeline: "8 months"
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white font-sans">
      <SEO 
        title="Marketing Intelligence - AI-Driven Digital Marketing | Qivra Technologies"
        description="Transform your marketing with Qivra's AI-powered Marketing Intelligence. Drive growth with intelligent campaigns, advanced analytics, and automation."
        keywords="marketing as a service, digital marketing, AI marketing, performance marketing, marketing automation, SEO services, PPC management"
      />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-24">
        {/* Ultra-Futuristic Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-950 to-black">
          {/* Animated Grid Pattern */}
          <div 
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `
                linear-gradient(rgba(0, 255, 255, 0.1) 1px, transparent 1px),
                linear-gradient(90deg, rgba(0, 255, 255, 0.1) 1px, transparent 1px),
                radial-gradient(circle at 25% 25%, rgba(34, 211, 238, 0.15) 0%, transparent 50%),
                radial-gradient(circle at 75% 75%, rgba(168, 85, 247, 0.15) 0%, transparent 50%)
              `,
              backgroundSize: '40px 40px, 40px 40px, 100% 100%, 100% 100%',
              animation: 'gridPulse 4s ease-in-out infinite alternate'
            }}
          />
          
          {/* Holographic Overlay Effects */}
          <div className="absolute inset-0">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent opacity-60 animate-pulse"></div>
            <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-purple-500 to-transparent opacity-60 animate-pulse" style={{ animationDelay: '2s' }}></div>
            <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-transparent via-blue-400 to-transparent opacity-40 animate-pulse" style={{ animationDelay: '1s' }}></div>
            <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-transparent via-violet-400 to-transparent opacity-40 animate-pulse" style={{ animationDelay: '3s' }}></div>
          </div>
          
          {/* Digital Particles */}
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(12)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-2 h-2 bg-cyan-400 rounded-full opacity-60"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                }}
                animate={{
                  y: [0, -100, 0],
                  opacity: [0.6, 1, 0.6],
                  scale: [1, 1.5, 1]
                }}
                transition={{
                  duration: 3 + Math.random() * 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: Math.random() * 2
                }}
              />
            ))}
            {[...Array(8)].map((_, i) => (
              <motion.div
                key={`purple-${i}`}
                className="absolute w-1 h-1 bg-purple-400 rounded-full opacity-80"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                }}
                animate={{
                  x: [0, 50, 0],
                  y: [0, -80, 0],
                  opacity: [0.8, 1, 0.8],
                }}
                transition={{
                  duration: 5 + Math.random() * 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: Math.random() * 3
                }}
              />
            ))}
          </div>
          
          {/* Floating Logo Elements - Enhanced */}
          <div className="absolute inset-0 overflow-hidden">
            {/* Top Area */}
            <motion.div
              className="absolute top-22 left-12 opacity-8"
              animate={{
                y: [0, -25, 0],
                x: [0, 17, 0],
                rotate: [0, 3, 0]
              }}
              transition={{
                duration: 13,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              style={{ filter: 'drop-shadow(0 0 10px rgba(0, 255, 255, 0.5))' }}
            >
              <QivratechLogo variant="mark" size="default" theme="dark" />
            </motion.div>

            <motion.div
              className="absolute top-36 right-16 opacity-10"
              animate={{
                y: [0, -21, 0],
                x: [0, -13, 0],
                rotate: [0, -5, 0]
              }}
              transition={{
                duration: 17,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 2.5
              }}
              style={{ filter: 'drop-shadow(0 0 15px rgba(168, 85, 247, 0.6))' }}
            >
              <QivratechLogo variant="mark" size="large" theme="dark" />
            </motion.div>

            {/* Middle Area */}
            <motion.div
              className="absolute top-1/2 left-8 opacity-6"
              animate={{
                y: [0, -35, 0],
                x: [0, 27, 0],
                rotate: [0, 9, 0]
              }}
              transition={{
                duration: 18,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 3
              }}
              style={{ filter: 'drop-shadow(0 0 12px rgba(34, 211, 238, 0.4))' }}
            >
              <QivratechLogo variant="mark" size="large" theme="dark" />
            </motion.div>

            {/* Bottom Area */}
            <motion.div
              className="absolute bottom-34 left-14 opacity-9"
              animate={{
                y: [0, -31, 0],
                x: [0, 23, 0],
                rotate: [0, 9, 0]
              }}
              transition={{
                duration: 11,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 5
              }}
              style={{ filter: 'drop-shadow(0 0 20px rgba(0, 255, 255, 0.7))' }}
            >
              <QivratechLogo variant="mark" size="default" theme="dark" />
            </motion.div>

            <motion.div
              className="absolute bottom-22 right-18 opacity-12"
              animate={{
                y: [0, -37, 0],
                x: [0, -19, 0],
                rotate: [0, -7, 0]
              }}
              transition={{
                duration: 15,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 4.5
              }}
              style={{ filter: 'drop-shadow(0 0 18px rgba(168, 85, 247, 0.8))' }}
            >
              <QivratechLogo variant="mark" size="small" theme="dark" />
            </motion.div>
          </div>
        </div>
        
        {/* Futuristic Hero Content */}
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Holographic Badge */}
            <motion.div 
              className="inline-flex items-center gap-3 bg-gradient-to-r from-cyan-500/30 to-purple-600/30 px-6 py-3 rounded-full border-2 border-cyan-400/50 mb-8 backdrop-blur-lg"
              animate={{
                boxShadow: [
                  '0 0 20px rgba(0, 255, 255, 0.5)',
                  '0 0 40px rgba(168, 85, 247, 0.5)',
                  '0 0 20px rgba(0, 255, 255, 0.5)'
                ],
                borderColor: [
                  'rgba(34, 211, 238, 0.5)',
                  'rgba(168, 85, 247, 0.5)',
                  'rgba(34, 211, 238, 0.5)'
                ]
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <motion.span 
                className="w-3 h-3 bg-cyan-400 rounded-full"
                animate={{ 
                  scale: [1, 1.5, 1],
                  opacity: [1, 0.5, 1]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
              <span className="text-cyan-400 text-sm font-light tracking-wider uppercase">
                Next-Gen Marketing Intelligence
              </span>
            </motion.div>
            
            {/* Holographic Title */}
            <motion.h1 
              className="text-6xl md:text-7xl lg:text-8xl font-extralight mb-8 leading-tight tracking-wide"
              animate={{
                textShadow: [
                  '0 0 20px rgba(0, 255, 255, 0.8)',
                  '0 0 40px rgba(168, 85, 247, 0.8)',
                  '0 0 20px rgba(0, 255, 255, 0.8)'
                ]
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <span className="text-white">Marketing</span>
              <br />
              <motion.span 
                className="bg-gradient-to-r from-cyan-300 via-blue-400 to-purple-500 bg-clip-text text-transparent font-light"
                animate={{
                  backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
                }}
                transition={{
                  duration: 6,
                  repeat: Infinity,
                  ease: "linear"
                }}
                style={{ backgroundSize: '200% 200%' }}
              >
                Intelligence
              </motion.span>
            </motion.h1>
            
            {/* Glowing Description */}
            <motion.p 
              className="text-2xl text-gray-300 max-w-4xl mx-auto font-light leading-relaxed tracking-wide mb-12"
              style={{
                textShadow: '0 0 10px rgba(255, 255, 255, 0.3)'
              }}
            >
              Intelligence unleashed through{" "}
              <motion.span 
                className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500"
                animate={{
                  textShadow: [
                    '0 0 10px rgba(0, 255, 255, 0.6)',
                    '0 0 20px rgba(168, 85, 247, 0.6)',
                    '0 0 10px rgba(0, 255, 255, 0.6)'
                  ]
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                quantum AI algorithms
              </motion.span>
              {" "}that amplify growth and maximize ROI beyond conventional limits
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Services Showcase */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-black mb-6">
              <span className="bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">
                Marketing Services
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Comprehensive marketing solutions powered by artificial intelligence and data-driven insights
            </p>

            {/* Service Navigation */}
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              {marketingServices.map((service, idx) => (
                <motion.button
                  key={service.id}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setActiveService(idx)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all ${
                    activeService === idx
                      ? 'bg-gradient-to-r from-blue-400 to-purple-600 text-white shadow-lg shadow-blue-400/50'
                      : 'bg-gray-800/50 text-gray-300 hover:text-blue-400 border border-gray-700 hover:border-blue-400/50'
                  }`}
                >
                  <div className="w-5 h-5">{React.cloneElement(service.icon, { className: "w-5 h-5" })}</div>
                  <span className="hidden sm:inline text-sm">{service.title.split(' ')[0]} {service.title.split(' ')[1]}</span>
                </motion.button>
              ))}
            </div>
          </motion.div>

          {/* Active Service Details */}
          <motion.div
            key={activeService}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className={`bg-gradient-to-br ${marketingServices[activeService].bgGradient} rounded-3xl border border-blue-400/20 p-8 md:p-12`}
          >
            <div className="grid lg:grid-cols-2 gap-12">
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="text-blue-400">
                    {marketingServices[activeService].icon}
                  </div>
                  <div>
                    <h3 className="text-3xl font-black text-white">
                      {marketingServices[activeService].title}
                    </h3>
                    <p className="text-xl text-blue-400 mt-2">
                      {marketingServices[activeService].subtitle}
                    </p>
                  </div>
                </div>
                
                <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                  {marketingServices[activeService].description}
                </p>

                <div className="grid grid-cols-2 gap-4 mb-8">
                  {marketingServices[activeService].metrics.map((metric, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: idx * 0.1 }}
                      className="bg-black/30 p-4 rounded-2xl border border-gray-700 text-center"
                    >
                      <div className="text-2xl font-black text-blue-400 mb-1">
                        {metric.value}
                      </div>
                      <div className="text-sm text-gray-300 mb-1">{metric.label}</div>
                      <div className="text-xs text-gray-500 capitalize">{metric.improvement}</div>
                    </motion.div>
                  ))}
                </div>

                <div className="mb-8">
                  <h4 className="text-xl font-bold text-white mb-4">Technologies Used</h4>
                  <div className="flex flex-wrap gap-2">
                    {marketingServices[activeService].technologies.map((tech, idx) => (
                      <span
                        key={idx}
                        className="bg-gradient-to-r from-blue-500/20 to-purple-600/20 text-blue-300 px-3 py-1 rounded-full text-sm border border-blue-400/30"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-xl font-bold text-white mb-6">Key Features & Capabilities</h4>
                <div className="space-y-3">
                  {marketingServices[activeService].features.map((feature, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      className="flex items-start gap-3 p-3 bg-black/20 rounded-lg"
                    >
                      <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300 text-sm">{feature}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Marketing Channels */}
      <section className="py-24 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-black mb-6">
              <span className="bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">
                Marketing Channels
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Multi-channel marketing approach with integrated analytics and optimization
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {marketingChannels.map((channel, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
                className="bg-gradient-to-br from-gray-800 to-gray-900 p-8 rounded-3xl border border-gray-700 hover:border-blue-400/50 transition-all"
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-400 to-purple-600 rounded-lg flex items-center justify-center">
                    <div className="text-white">{channel.icon}</div>
                  </div>
                  <h3 className="text-lg font-bold text-white">{channel.channel}</h3>
                </div>
                
                <p className="text-gray-400 mb-6">{channel.description}</p>
                
                <div className="space-y-2">
                  {channel.metrics.map((metric, metricIdx) => (
                    <div key={metricIdx} className="flex justify-between items-center py-2 border-b border-gray-700 last:border-b-0">
                      <span className="text-sm text-gray-400">{metric.split(':')[0]}:</span>
                      <span className="text-sm font-bold text-blue-400">{metric.split(':')[1]}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-black mb-6">
              <span className="bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">
                Our Process
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Proven methodology that delivers consistent results and measurable growth
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.2 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="relative mb-8">
                  <div className="w-20 h-20 mx-auto bg-gradient-to-r from-blue-400 to-purple-600 rounded-full flex items-center justify-center mb-4">
                    <span className="text-2xl font-black text-white">{step.step}</span>
                  </div>
                  <div className="w-12 h-12 mx-auto bg-gradient-to-r from-blue-500/20 to-purple-600/20 rounded-full flex items-center justify-center border border-blue-400/30">
                    <div className="text-blue-400">{step.icon}</div>
                  </div>
                </div>
                
                <h3 className="text-xl font-bold text-white mb-4">{step.title}</h3>
                <p className="text-gray-400 mb-6 text-sm leading-relaxed">{step.description}</p>
                
                <div className="space-y-2">
                  {step.deliverables.map((deliverable, delIdx) => (
                    <div key={delIdx} className="text-xs text-blue-400 bg-blue-500/10 px-3 py-1 rounded-full border border-blue-400/20">
                      {deliverable}
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-24 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-black mb-6">
              <span className="bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">
                Success Stories
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Real results from our Marketing Intelligence clients
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8">
            {caseStudies.map((study, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.2 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-gray-800 to-gray-900 p-8 rounded-3xl border border-gray-700"
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-400 to-purple-600 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">{study.client}</h3>
                    <p className="text-blue-400">{study.industry}</p>
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-white mb-2">Challenge</h4>
                  <p className="text-gray-400 mb-4">{study.challenge}</p>
                  
                  <h4 className="text-lg font-semibold text-white mb-2">Solution</h4>
                  <p className="text-gray-400 mb-6">{study.solution}</p>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  {study.results.map((result, resultIdx) => (
                    <div key={resultIdx} className="bg-blue-500/10 p-4 rounded-lg border border-blue-400/20 text-center">
                      <div className="text-lg font-black text-blue-400 mb-1">{result.value}</div>
                      <div className="text-xs text-gray-400">{result.metric}</div>
                    </div>
                  ))}
                </div>

                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Clock className="w-4 h-4" />
                  <span>Timeline: {study.timeline}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-blue-500/10 to-purple-600/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-black text-white mb-6">
              Ready to Accelerate Your Growth?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Let our AI-powered marketing experts create a custom strategy that delivers 
              measurable results and exceptional ROI for your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-blue-400 to-purple-600 px-8 py-4 rounded-full font-bold text-white hover:shadow-xl hover:shadow-blue-400/50 transition-all flex items-center gap-2 justify-center"
              >
                Start Your Marketing Transformation
                <ArrowRight className="w-5 h-5" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-blue-400 text-blue-400 px-8 py-4 rounded-full font-bold hover:bg-blue-400 hover:text-black transition-all"
              >
                Schedule Free Consultation
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default MarketingPage;